// Member — the resolved-user shape returned by `/members/:id` and consumed
// by the LinkableKind 'member' provider.
import { z } from 'zod';
import { DisplayName, Locale, Username } from '../auth/requests.js';
import { ProfileAvatarDesign } from './avatar.js';
import { RoleId } from './role.js';
// ---------------------------------------------------------------------------
// Account status enum
// ---------------------------------------------------------------------------
export const MemberStatus = z.enum(['active', 'pending', 'banned']);
export const MemberAccountContactStatus = z.enum(['verified', 'unverified', 'notBound']);
export const MemberAccountContact = z.object({
    value: z.string().nullable(),
    maskedValue: z.string().nullable(),
    status: MemberAccountContactStatus,
    verifiedAt: z.string().datetime({ offset: true }).nullable(),
    canAssistVerification: z.boolean(),
});
export const MemberAccount = z.object({
    username: z.string().nullable(),
    locale: Locale,
    email: MemberAccountContact,
    phone: MemberAccountContact,
    requiresVerification: z.boolean(),
});
export const MemberManagement = z.object({
    canEditBasics: z.boolean(),
    canAssistVerification: z.boolean(),
    canChangeRole: z.boolean(),
    canBan: z.boolean(),
    assignableRoles: z.array(RoleId),
});
export const UpdateMemberAccountRequest = z
    .object({
    displayName: DisplayName.optional(),
    username: Username.optional(),
    locale: Locale.optional(),
})
    .superRefine((val, ctx) => {
    if (val.displayName === undefined &&
        val.username === undefined &&
        val.locale === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one account field is required',
        });
    }
});
export const AssistMemberVerificationRequest = z.object({
    kind: z.enum(['email', 'phone']),
});
export const ApproveMemberVerificationRequest = z.object({
    kind: z.enum(['email', 'phone']),
});
export const MEMBER_ACTIVITY_KINDS = {
    AccountUpdated: 'member.account-updated',
    VerificationAssisted: 'member.verification-assisted',
    VerificationApproved: 'member.verification-approved',
    RoleChanged: 'member.role-changed',
    Banned: 'member.banned',
    Unbanned: 'member.unbanned',
};
// ---------------------------------------------------------------------------
// Member — full canonical shape. Pages always consume a redacted variant
// based on canRead (parents see private fields; members do not).
// ---------------------------------------------------------------------------
export const Member = z.object({
    id: z.string().uuid(),
    /** Display name. */
    name: z.string().min(1).max(80),
    /** 2-letter avatar fallback. */
    avatar: z.string().min(1).max(4),
    /** Public avatar styling from the member homepage profile. */
    avatarDesign: ProfileAvatarDesign,
    /** Email — null in redacted view. */
    email: z.string().email().nullable(),
    roles: z.array(RoleId),
    /** UI-default role (governs which menu the user sees on login). */
    primaryRole: RoleId,
    /** 'active' / 'pending' (invited not redeemed) / 'banned'. */
    status: MemberStatus,
    /** ISO 8601. */
    joined: z.string().datetime({ offset: true }),
    /** Free-form last-seen string ("now", "12m", "yesterday"). */
    lastSeen: z.string(),
    /** Phone — null unless self / parent / staff. */
    phone: z.string().nullable(),
    /** City — null unless self / parent / staff. */
    city: z.string().nullable(),
    /** Internal staff note. */
    note: z.string().nullable(),
    /** For child members (role: 'child'); else null. */
    age: z.number().int().min(0).max(150).nullable(),
    // ---- Bindings ----
    /** Parent → children (role: 'parent' only). */
    parentOf: z.array(z.string().uuid()),
    /** Child → parents (role: 'child' only). */
    childOf: z.array(z.string().uuid()),
    // ---- Group attachments ----
    /** Manager-managed group ids. */
    manages: z.array(z.string().uuid()),
    /** Employee staff-of group ids (drives attendance.mark canWrite). */
    worksIn: z.array(z.string().uuid()),
    /** Child enrollment group ids. */
    enrolledIn: z.array(z.string().uuid()),
    /** Account identifiers and verification state, redacted per viewer. */
    account: MemberAccount,
    /** Per-viewer management affordances for this member. */
    management: MemberManagement,
});
/** Redacted shape suitable for non-staff / non-parent viewers. */
export const MemberRedacted = Member.omit({
    email: true,
    phone: true,
    city: true,
    note: true,
});
//# sourceMappingURL=user.js.map